import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Bell, Menu, PlusCircle, LayoutDashboard, User, Shield, LogOut, LogIn, MapPin, Heart, BookHeart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Badge } from "./ui/badge";
import InviteFriend from "./InviteFriend";

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openNotif = async (n) => {
    if (n.link) navigate(n.link);
    await api.post("/notifications/read");
    load();
  };

  return (
    <Popover onOpenChange={(o) => { if (!o && unread) { api.post("/notifications/read").then(load); } }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative p-2 rounded-full hover:bg-muted transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-96 overflow-y-auto">
        <div className="px-4 py-3 border-b font-serif font-semibold">Notifiche</div>
        {items.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">Nessuna notifica</div>}
        {items.map((n) => (
          <button key={n.id} data-testid="notification-item" onClick={() => openNotif(n)}
            className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-muted transition-colors ${!n.read ? "bg-accent/10" : ""}`}>
            <p className="text-sm">{n.text}</p>
            <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</p>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); setOpen(false); navigate("/"); };

  const NavLinks = ({ onClick }) => (
    <>
      <Link to="/" onClick={onClick} data-testid="nav-catalogo" className="text-sm font-medium hover:text-primary transition-colors">Catalogo</Link>
      <Link to="/mappa" onClick={onClick} data-testid="nav-mappa" className="text-sm font-medium hover:text-primary transition-colors">Mappa</Link>
      <Link to="/dona" onClick={onClick} data-testid="nav-dona" className="text-sm font-medium hover:text-primary transition-colors">Dona i tuoi libri</Link>
      {user && <Link to="/desideri" onClick={onClick} data-testid="nav-desideri" className="text-sm font-medium hover:text-primary transition-colors">Lista desideri</Link>}
      {user && <Link to="/avvisi" onClick={onClick} data-testid="nav-avvisi" className="text-sm font-medium hover:text-primary transition-colors">Avvisi</Link>}
      {user && <Link to="/pubblica" onClick={onClick} data-testid="nav-pubblica" className="text-sm font-medium hover:text-primary transition-colors">Pubblica un libro</Link>}
      {user && <Link to="/area-personale" onClick={onClick} data-testid="nav-area-personale" className="text-sm font-medium hover:text-primary transition-colors">Area personale</Link>}
      {user?.role === "admin" && <Link to="/admin" onClick={onClick} data-testid="nav-admin" className="text-sm font-medium text-primary hover:underline">Amministrazione</Link>}
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><BookOpen className="w-5 h-5" /></span>
          <span className="font-serif text-lg sm:text-xl font-bold tracking-tight">Libri in Circolo</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLinks />
        </nav>

        <div className="flex items-center gap-2">
          <InviteFriend variant="ghost" size="sm" className="hidden md:inline-flex" />
          {user ? (
            <>
              <NotificationsBell />
              <div className="hidden md:flex items-center gap-2">
                <Link to="/profilo" data-testid="nav-profilo" className="text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-muted transition-colors max-w-[140px] truncate">{user.name}</Link>
                <Button data-testid="logout-button" variant="ghost" size="icon" onClick={doLogout}><LogOut className="w-4 h-4" /></Button>
              </div>
            </>
          ) : (
            <Button asChild data-testid="nav-accedi" className="hidden md:inline-flex bg-primary hover:bg-primary/90">
              <Link to="/accedi"><LogIn className="w-4 h-4 mr-2" />Accedi</Link>
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="md:hidden"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col">
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0"><SheetTitle className="font-serif text-left">Menu</SheetTitle></SheetHeader>
              <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1">
                <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><BookOpen className="w-4 h-4" />Catalogo</Link>
                <Link to="/mappa" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><MapPin className="w-4 h-4" />Mappa</Link>
                <Link to="/dona" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><BookHeart className="w-4 h-4" />Dona i tuoi libri</Link>
                {user && <Link to="/desideri" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><Heart className="w-4 h-4" />Lista desideri</Link>}
                {user && <Link to="/avvisi" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><Bell className="w-4 h-4" />Avvisi</Link>}
                {user && <Link to="/pubblica" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><PlusCircle className="w-4 h-4" />Pubblica un libro</Link>}
                {user && <Link to="/area-personale" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><LayoutDashboard className="w-4 h-4" />Area personale</Link>}
                {user && <Link to="/profilo" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted"><User className="w-4 h-4" />Profilo</Link>}
                {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted text-primary"><Shield className="w-4 h-4" />Amministrazione</Link>}
                <div className="border-t border-border my-2" />
                <InviteFriend variant="ghost" size="default" align="start" className="w-full justify-start px-3 py-3 h-auto font-normal" />
              </div>
              <div className="shrink-0 border-t border-border p-4 bg-background">
                {user ? (
                  <>
                    <div className="flex items-center gap-2 px-1 pb-3 text-sm text-muted-foreground">
                      <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0"><User className="w-4 h-4" /></span>
                      <span className="truncate">{user.name}</span>
                    </div>
                    <Button data-testid="mobile-logout-button" onClick={doLogout} variant="outline" className="w-full h-12 justify-center text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive text-base">
                      <LogOut className="w-5 h-5 mr-2" />Esci
                    </Button>
                  </>
                ) : (
                  <Button asChild data-testid="mobile-accedi-button" className="w-full h-12 justify-center bg-primary hover:bg-primary/90 text-base">
                    <Link to="/accedi" onClick={() => setOpen(false)}><LogIn className="w-5 h-5 mr-2" />Accedi</Link>
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
